import React, {ReactNode} from "react";
import {Box} from "@mui/material";
import {capitalize} from "lodash";
import {getAssociationIds, CHANNEL_PILL_SX, filterRealFiles} from "../videoProduction/helper";
import ListItemCard, {ListItemCardProps} from "../../components/ListItemCard";
import {BrandOrderItem, getBrandImageUrl} from "./index";

interface OrderListItemProps {
    order: BrandOrderItem;
    contentTypes: string[];
    channelNames?: string[];
    rightContent?: ReactNode;
    // A soft-deleted order (the admin Deleted view): tagged, and its
    // videos are not flagged for review since nothing on it can be acted on.
    deleted?: boolean;
}

const DELETED_CHIP_SX = {
    backgroundColor: "var(--down-bg)",
    color: "var(--down)",
    fontWeight: 500,
};

const startCase = (text: string) =>
    text.replace(/\b\w+(?:'\w+)?\b/g, (word: string) => capitalize(word));

const OrderListItem = ({
    order,
    contentTypes,
    channelNames = [],
    rightContent,
    deleted = false,
}: OrderListItemProps) => {
    const brands = order.brand ? [order.brand] : [];
    const projectCount = order.projects?.length ?? 0;

    const allVideos = order.projects?.flatMap((p) => filterRealFiles(p.projectFiles)) || [];
    const totalVideos = allVideos.length;
    const unapprovedVideos = allVideos.filter((v) => !v.final).length;

    const chips: ListItemCardProps["chips"] = [];
    if (deleted) {
        chips.push({label: "Deleted", chipSx: DELETED_CHIP_SX});
    }
    if (projectCount > 0) {
        chips.push({
            label: `${projectCount} ${
                projectCount === 1 ? "Project" : "Projects"
            }`,
        });
    }
    if (totalVideos > 0) {
        const videoLabel = `${totalVideos} ${totalVideos === 1 ? "Video" : "Videos"}`;
        chips.push({
            label: unapprovedVideos > 0 && !deleted ? (
                <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                    <span>{`Review - ${videoLabel}`}</span>
                    <Box
                        sx={{
                            backgroundColor: "#EF4444",
                            color: "#fff",
                            borderRadius: "50%",
                            width: 18,
                            height: 18,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1,
                        }}
                    >
                        {unapprovedVideos}
                    </Box>
                </Box>
            ) as any : videoLabel,
        });
    }
    if (order.sow?.name) {
        chips.push({label: order.sow.name});
    }
    for (const type of contentTypes) {
        chips.push({
            label: startCase(type.replace(/_/i, " ")),
            variant: "secondary",
        });
    }
    for (const ch of channelNames) {
        chips.push({
            label: ch,
            chipSx: CHANNEL_PILL_SX,
        });
    }
    return (
        <ListItemCard
            image={getBrandImageUrl(order.brand)}
            title={order.name || "Untitled Order"}
            associationIds={getAssociationIds({
                id: order.id,
                brands,
                product: {title: order.brand?.name ?? ""},
                order: {sow: order.sow || null},
            })}
            brands={brands}
            brandsNavigable
            chips={chips}
            rightContent={rightContent}
        />
    );
};

export default OrderListItem;
